/**
 * The mock content generator behind the Generator port (`ListingContentGenerator`).
 * A pure, deterministic stub: the same prize details always draft the same
 * copy, and it never touches the network. It stands in for a real provider
 * chosen in a later ADR (pivot doc §6 Stage 3, assumption 4).
 */

import type { ListingContentGenerator, ListingCopy, ListingPrizeDetails } from '@/api/listings'
import { formatCurrency } from '@/design/format'

import { hashString } from '../random'

function pick<T>(options: readonly T[], key: string): T {
  const list = options.length > 0 ? options : ([undefined] as unknown as readonly T[])
  return list[hashString(key) % list.length] as T
}

const HOOKS = [
  'Your chance to win',
  "Don't miss your shot at",
  'Enter now for your chance to win',
  'Here is your chance to win',
]

const URGENCY = [
  'Entries are limited, so get yours in early.',
  'Once the max entries are hit, that is it — no more tickets.',
  'Tickets are moving fast, so do not wait to enter.',
]

const CTAS = ['Enter today.', 'Grab your tickets now.', 'Get your entry in before it closes.']

function money(value: number): string {
  return formatCurrency(value, { currency: 'GBP', locale: 'en-GB' })
}

export const mockListingContentGenerator: ListingContentGenerator = {
  generate(prize: ListingPrizeDetails, tenantName: string): ListingCopy {
    const key = `${prize.prizeName}|${prize.prizeCategory}|${prize.ticketPrice}|${prize.maxEntries}`
    const hook = pick(HOOKS, `${key}|hook`)
    const urgency = pick(URGENCY, `${key}|urgency`)
    const cta = pick(CTAS, `${key}|cta`)
    const points = prize.keySellingPoints.length > 0 ? prize.keySellingPoints : [prize.prizeName]
    const valuePhrase = `worth ${money(prize.prizeValue)}`
    const priceLine = `Tickets from ${money(prize.ticketPrice)}, with ${prize.maxEntries.toLocaleString('en-GB')} entries available.`

    const title = `Win ${prize.prizeName}`
    const shortDescription = `${hook} ${prize.prizeName} ${valuePhrase}. ${priceLine}`
    const longDescription = [
      `${shortDescription}`,
      points.map((point) => `• ${point}`).join('\n'),
      prize.instantWinsCount > 0
        ? `Plus ${prize.instantWinsCount} instant wins along the way, so you could win before the main draw.`
        : null,
      `The draw takes place on ${prize.drawDate}. ${urgency}`,
    ]
      .filter(Boolean)
      .join('\n\n')

    return {
      title,
      shortDescription,
      longDescription,
      seoTitle: `${prize.prizeName} Competition | Win ${valuePhrase} | ${tenantName}`,
      seoDescription: shortDescription.slice(0, 155),
      keyPoints: points,
      emailCopy: `Subject: ${hook} ${prize.prizeName}\n\n${shortDescription} ${cta}`,
      pushCopy: `${hook} ${prize.prizeName}! ${priceLine} ${cta}`,
      socialCopy: `${hook} ${prize.prizeName} ${valuePhrase}! ${cta} #competition #win${prize.prizeCategory.replace(/\s+/g, '')}`,
      adCopy: `${title} — ${valuePhrase}. ${priceLine} ${cta}`,
      offlineFlyerHeadline: `WIN ${prize.prizeName.toUpperCase()}`,
    }
  },
}
